// AHA-6796 ducted design-load coverage display helpers. The metric is
// server-computed (Phase 6: capacity / design load, capped at 100) and is a
// DISTINCT concept from the ductless hours-weighted heatingScore/
// coolingScore — null means "not computable" and renders as "—", never 0.
// Reading it off a row lives with the other row readers in
// ducted-system-metrics.ts; both metrics are displayed together by
// ducted-performance-display.ts.

// design_loads: the project's Manual J; tonnage_estimate: from the existing
// equipment's nominal tonnage; simplified_estimate: an assumed-house Manual J
// built from the home-details answers alone.
export type DuctedCoverageBasis =
  | 'design_loads'
  | 'tonnage_estimate'
  | 'simplified_estimate';

export type DuctedCoverageType = {
  heatingCoveragePercent: number | null;
  coolingCoveragePercent: number | null;
  basis: DuctedCoverageBasis;
  heatingRequirementBtu?: number | null;
  coolingRequirementBtu?: number | null;
};

// Reader for the new SystemOption/SelectedSystem field. Cast required until
// @alpinehomeair/common is republished with ductedCoverage on
// SystemOptionSchema; centralizing it here keeps the cast in one place.
export function getDuctedCoverage(
  system: object | null | undefined
): DuctedCoverageType | undefined {
  if (!system) return undefined;
  return (system as { ductedCoverage?: DuctedCoverageType }).ductedCoverage;
}

export function getZoneDuctedCoverage(
  zone: object | null | undefined
): DuctedCoverageType | undefined {
  if (!zone) return undefined;
  return (zone as { ductedCoverage?: DuctedCoverageType }).ductedCoverage;
}

export function stampZoneDuctedCoverage(
  zone: object,
  coverage: DuctedCoverageType
): void {
  Object.assign(zone, { ductedCoverage: coverage });
}

export function ductedPercentLabel(value: number | null): string {
  return value === null ? '—' : `${Math.floor(value)}%`;
}

type RequirementKey = 'heatingRequirementBtu' | 'coolingRequirementBtu';

// The load a side was matched against travels with its percent: the card's
// when the card carries one, the saved figure otherwise, and no key at all
// when neither side has one.
function mergedRequirement(
  key: RequirementKey,
  saved: DuctedCoverageType,
  displayed: DuctedCoverageType
): Partial<Pick<DuctedCoverageType, RequirementKey>> {
  if (displayed[key] === undefined && saved[key] === undefined) return {};
  return { [key]: displayed[key] ?? saved[key] ?? null };
}

// The combination card and the save that manufactures the system compute
// design-load coverage against the same requirements but reach the capacities
// by different routes, so one can resolve a side the other cannot. The figure
// the user already saw wins — a system must not change its headline the
// moment it is added — and the saved figure fills whatever the card left null.
// The requirement follows the same rule, so the load the zone table shows is
// the one the percents beside it divide by.
export function mergeDuctedCoverage(
  saved: DuctedCoverageType | null | undefined,
  displayed: DuctedCoverageType | null | undefined
): DuctedCoverageType | null {
  if (!saved) return displayed ?? null;
  if (!displayed) return saved;
  const displayedResolvedASide =
    displayed.heatingCoveragePercent !== null ||
    displayed.coolingCoveragePercent !== null;
  return {
    ...saved,
    heatingCoveragePercent:
      displayed.heatingCoveragePercent ?? saved.heatingCoveragePercent,
    coolingCoveragePercent:
      displayed.coolingCoveragePercent ?? saved.coolingCoveragePercent,
    basis: displayedResolvedASide ? displayed.basis : saved.basis,
    ...mergedRequirement('heatingRequirementBtu', saved, displayed),
    ...mergedRequirement('coolingRequirementBtu', saved, displayed)
  };
}

export const DUCTED_HEATING_TOOLTIP =
  "Compares this system's rated heating capacity to your home's design " +
  'heating load — the heat needed on a near-worst-case winter day (99% ' +
  'design conditions). 100% means the system fully covers that load; ' +
  'values are capped at 100%.';

export const DUCTED_COOLING_TOOLTIP =
  "Compares this system's rated cooling capacity to your home's design " +
  'cooling load — the cooling needed on a near-worst-case summer day (1% ' +
  'design conditions). 100% means the system fully covers that load; ' +
  'values are capped at 100%.';

export const DUCTED_TONNAGE_ESTIMATE_TOOLTIP_SUFFIX =
  'Room-by-room sizing was skipped, so the load is estimated from the ' +
  'selected tonnage (tons × 12,000 BTU/h), with the heating load scaled ' +
  "by a factor for your region's winter design temperature.";

export const DUCTED_COVERAGE_UNAVAILABLE_TOOLTIP =
  "Coverage can't be calculated — the capacity rating for this equipment " +
  "(or your home's load information) isn't available.";

export const DUCTED_SIMPLIFIED_ESTIMATE_TOOLTIP_SUFFIX =
  'Room-by-room sizing was skipped, so the load is estimated from your ' +
  'home details (square footage, floors, foundation, age and insulation) ' +
  'using typical construction for homes like yours in your climate.';

const DUCTED_BASIS_TOOLTIP_SUFFIX: Partial<
  Record<DuctedCoverageBasis, string>
> = {
  tonnage_estimate: DUCTED_TONNAGE_ESTIMATE_TOOLTIP_SUFFIX,
  simplified_estimate: DUCTED_SIMPLIFIED_ESTIMATE_TOOLTIP_SUFFIX
};

// Where an estimated load came from, for any surface that shows the load a
// system was matched against; null for a real design-load calculation, which
// needs no caveat.
export function ductedCoverageBasisNote(
  basis: DuctedCoverageBasis | null | undefined
): string | null {
  return basis ? DUCTED_BASIS_TOOLTIP_SUFFIX[basis] ?? null : null;
}

export function ductedCoverageTooltipText(
  type: 'heating' | 'cooling',
  chip: { value: number | null; basis: DuctedCoverageBasis }
): string {
  if (chip.value === null) return DUCTED_COVERAGE_UNAVAILABLE_TOOLTIP;
  const base =
    type === 'heating' ? DUCTED_HEATING_TOOLTIP : DUCTED_COOLING_TOOLTIP;
  const suffix = ductedCoverageBasisNote(chip.basis);
  return suffix ? `${base} ${suffix}` : base;
}
